import { useCallback, useEffect, useRef, useState } from 'react';

import { DataSource } from '@futrli-sdk/organisations';
import { ExcelUploadsListResponse } from '@futrli-sdk/predict';

import { fetchOrganisationById } from '../../../../../../core/api/workspace-coordinator';
import { getExcelFilesList } from '../../../../../../core/api/organisations';

const REFRESH_INTERVAL = 10000; // 10 seconds

export const useDatasourcePolling = (workspaceId?: string, organisationId?: string) => {
  const [data, setData] = useState<{ datasource?: DataSource; excelFiles?: ExcelUploadsListResponse }>({});
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [polling, setPolling] = useState(false);

  const req = useRef<Promise<unknown>>();

  const loadData = useCallback(async (workspaceId: string, orgId: string) => {
    try {
      setIsLoading(true);
      let newExcelFiles: ExcelUploadsListResponse | undefined;

      const thisReq = (req.current = fetchOrganisationById(workspaceId, orgId));
      const newOrg = await thisReq;

      if (thisReq === req.current && newOrg.datasource.type === 'excel') {
        newExcelFiles = await getExcelFilesList(workspaceId, orgId);
      } else {
        newExcelFiles = undefined;
      }

      if (thisReq === req.current) {
        setData({
          datasource: newOrg.datasource,
          excelFiles: newExcelFiles
        });
        setIsLoading(false);
      }
    } catch {
      setError(true);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!workspaceId || !organisationId) {
      return;
    }

    // Periodically fetch data to determine when sync is finished
    /* eslint-disable @typescript-eslint/no-explicit-any */
    let id: any;
    if (polling) {
      id = setInterval(() => {
        loadData(workspaceId, organisationId);
      }, REFRESH_INTERVAL);
    } else {
      clearInterval(id);
    }

    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [loadData, organisationId, polling, workspaceId]);

  // reload the organisation on mount so we always have the latest sync date and status
  useEffect(() => {
    if (workspaceId && organisationId) {
      loadData(workspaceId, organisationId);
    }
  }, [loadData, organisationId, workspaceId]);

  return {
    datasource: data.datasource,
    error,
    excelFiles: data.excelFiles,
    isLoading,
    loadData,
    polling,
    setPolling
  };
};
